import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Bed, Bath, Square, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import DynamicForm from './DynamicForm';
import { usePropertyFormSubmission } from '../../hooks/useFormSubmission';

/**
 * Format price with commas (e.g., 399000 -> $399,000)
 */
function formatPrice(price) {
    if (price === undefined || price === null || price === '') return 'TBD';
    const n = Number(price);
    if (!isFinite(n)) return String(price);
    return `$${n.toLocaleString()}`;
}

/**
 * Public Property Detail Page
 * Displays a single property with hero image, details, and viewing form
 */
import { resolveFormFields, injectPropertyOptions } from './formUtils';

const PublicPropertyPage = ({ property, portfolio, agentName, properties }) => {
    const navigate = useNavigate();

    // Use centralized form submission hook
    const {
        submit: submitViewingForm,
        state: viewingFormState,
        error: viewingFormError,
        isSubmitting: isViewingFormSubmitting,
        isSuccess: isViewingFormSuccess,
        reset: resetViewingForm,
    } = usePropertyFormSubmission({
        property,
        portfolio,
        onSuccess: (result) => {
            console.log('[PublicPropertyPage] Viewing form submitted successfully:', result.responseId);
        },
        onError: (error) => {
            console.error('[PublicPropertyPage] Viewing form error:', error);
        },
    });

    if (!property) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-stone-900 mb-2">Property Not Found</h1>
                    <p className="text-stone-600 mb-6">This property is no longer available.</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to Listings
                    </button>
                </div>
            </div>
        );
    }

    const handleFormSubmit = async (payload) => {
        // payload: { responses: { [fieldId]: value } }
        const responses = payload.responses || {};
        await submitViewingForm(responses);
    };

    const heroImage = property.imageUrls && property.imageUrls[0] ? property.imageUrls[0] : null;

    return (
        <div className="bg-white">
            {/* Back Button */}
            <div className="sticky top-0 z-10 bg-white border-b border-stone-200 px-4 py-3">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 text-stone-700 hover:text-stone-900 font-semibold"
                >
                    <ChevronLeft className="w-5 h-5" />
                    Back to Listings
                </button>
            </div>

            <div className="max-w-6xl mx-auto">
                {/* Hero Image Section */}
                <div className="relative h-96 md:h-[420px] bg-stone-200 overflow-hidden">
                    {heroImage ? (
                        <img
                            src={heroImage}
                            alt={property.address || 'Property'}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-stone-300">
                            <span className="text-stone-500 text-lg">No image available</span>
                        </div>
                    )}

                    {/* Price Overlay (large white text with subtle shadow at bottom-left) */}
                    <div className="absolute left-6 bottom-6 text-white">
                        <p className="text-4xl md:text-5xl font-extrabold drop-shadow-[0_4px_8px_rgba(0,0,0,0.45)]">
                            {formatPrice(property.price)}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-4 md:p-8">
                    {/* Main Content (Left) */}
                    <div className="lg:col-span-2">
                        {/* Address Display */}
                        <div className="mb-8">
                            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-3">
                                {property.address || 'Property'}
                            </h1>
                            {property.address2 && (
                                <p className="text-lg text-stone-700 flex items-start gap-2">
                                    <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                    {property.address2}
                                </p>
                            )}
                        </div>

                        {/* Property Specs Pills */}
                        <div className="flex flex-wrap gap-3 mb-10">
                            {property.bedrooms !== undefined && (
                                <div className="bg-stone-100 px-4 py-3 rounded-md flex items-center gap-2">
                                    <Bed className="w-5 h-5 text-stone-700" />
                                    <span className="font-semibold text-sm text-stone-900">{property.bedrooms} Beds</span>
                                </div>
                            )}
                            {property.bathrooms !== undefined && (
                                <div className="bg-stone-100 px-4 py-3 rounded-md flex items-center gap-2">
                                    <Bath className="w-5 h-5 text-stone-700" />
                                    <span className="font-semibold text-sm text-stone-900">{property.bathrooms} Baths</span>
                                </div>
                            )}
                            {property.sqft && (
                                <div className="bg-stone-100 px-4 py-3 rounded-md flex items-center gap-2">
                                    <Square className="w-5 h-5 text-stone-700" />
                                    <span className="font-semibold text-sm text-stone-900">{(Number(property.sqft) && Number(property.sqft).toLocaleString()) || String(property.sqft)} Sq Ft</span>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div className="mb-8">
                            <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">About this property</h2>
                            <p className="text-stone-700 text-base md:text-lg leading-relaxed whitespace-pre-wrap">
                                {property.description || 'No description available for this property.'}
                            </p>
                        </div>

                        {/* Additional Images Gallery */}
                        {property.imageUrls && property.imageUrls.length > 1 && (
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-stone-900 mb-6">Gallery</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {property.imageUrls.slice(1).map((image, idx) => (
                                        <img
                                            key={idx}
                                            src={image}
                                            alt={`Property view ${idx + 2}`}
                                            className="w-full h-48 object-cover rounded-lg"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar (Right) - Schedule Viewing */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-20 bg-stone-50 p-6 rounded-lg border border-stone-200">
                            <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-8">Schedule a Viewing</h3>

                            {/* Success State */}
                            {isViewingFormSuccess && (
                                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center gap-3 mb-2">
                                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                                        <p className="text-green-800 font-medium">Request Submitted!</p>
                                    </div>
                                    <p className="text-green-700 text-sm">We'll contact you shortly to schedule your viewing.</p>
                                    <button
                                        onClick={resetViewingForm}
                                        className="mt-4 w-full py-2 border border-green-300 rounded-lg text-green-700 hover:bg-green-50 transition-colors text-sm"
                                    >
                                        Submit Another Request
                                    </button>
                                </div>
                            )}

                            {/* Error State */}
                            {viewingFormError && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                                    <div>
                                        <p className="text-red-800 font-medium">Submission Failed</p>
                                        <p className="text-red-700 text-sm">{viewingFormError}</p>
                                    </div>
                                </div>
                            )}

                            {/* Form - Hide after success */}
                            {!isViewingFormSuccess && (
                                (() => {
                                    // Resolve fields using property.captureFormFields priority, falling back to portfolio.captureFormFields, then legacy customFields
                                    let source = [];
                                    if (Array.isArray(property?.captureFormFields) && property.captureFormFields.length > 0) {
                                        source = property.captureFormFields;
                                    } else if (Array.isArray(portfolio?.captureFormFields) && portfolio.captureFormFields.length > 0) {
                                        source = portfolio.captureFormFields;
                                    } else if (Array.isArray(property?.customFields) && property.customFields.length > 0) {
                                        source = property.customFields;
                                    } else if (Array.isArray(portfolio?.customFields) && portfolio.customFields.length > 0) {
                                        source = portfolio.customFields;
                                    }

                                    const resolved = injectPropertyOptions(resolveFormFields(source), properties);

                                    return (
                                        <DynamicForm
                                            customFields={resolved}
                                            includeDefaults={false}
                                            initialValues={{ interestedProperty: property?.id ? [property.id] : [] }}
                                            onSubmit={handleFormSubmit}
                                            submitLabel={isViewingFormSubmitting ? 'Submitting...' : 'Submit Interest'}
                                            disabled={isViewingFormSubmitting}
                                        />
                                    )
                                })()
                            )};
                            {/* Agent Info (if available) */}
                            {agentName && (
                                <div className="mt-8 pt-6 border-t border-stone-300 text-center text-sm">
                                    <p className="text-stone-600 mb-2">Listed by</p>
                                    <p className="font-semibold text-stone-900">{agentName}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-stone-900 text-stone-300 py-8 px-4 text-center text-sm mt-16">
                <p>© 2026 Get Kaeros. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default PublicPropertyPage;
